#!/usr/bin/env node

import { Command, Option } from "commander";

import pkg from "../package.json" with { type: "json" };
import { add } from "./commands/add.js";
import { docs } from "./commands/docs.js";
import { init } from "./commands/init.js";
import { remove } from "./commands/remove.js";
import { search } from "./commands/search.js";
import { setup } from "./commands/setup.js";
import { update } from "./commands/update.js";

const program = new Command()
  .name("starwind")
  .description("Add beautifully designed components to your Astro applications")
  .version(pkg.version);

program
  .command("init")
  .description("Initialize your project with Starwind")
  .option("-d, --defaults", "Use default values for all prompts")
  .option("-p, --pro", "Initialize with Starwind Pro setup")
  .action((options) => init(false, { defaults: options.defaults, pro: options.pro }));

program
  .command("add")
  .description("Add Starwind components to your project")
  .argument("[components...]", "The components to add (space separated)")
  .allowExcessArguments()
  .option("-a, --all", "Add all available components")
  .option("-y, --yes", "Skip confirmation prompts")
  .option("-o, --overwrite", "Overwrite existing files")
  .addOption(
    new Option("-m, --package-manager <pm>", "Package manager to use").choices([
      "npm",
      "pnpm",
      "yarn",
    ]),
  )
  .action(add);

program
  .command("docs")
  .description("Open documentation for Starwind components")
  .argument("[components...]", "The components to look up (space separated)")
  .allowExcessArguments()
  .option("--json", "Output as JSON")
  .action(docs);

program
  .command("search")
  .description("Search Starwind components and Pro blocks")
  .argument("[query]", "Search query string")
  .allowExcessArguments()
  .option("-p, --plan <plan>", "Filter Pro blocks by plan type (free or pro)")
  .option("-c, --category <category>", "Filter Pro blocks by category")
  .option("-l, --limit <number>", "Maximum number of Pro blocks to display", "20")
  .option("-o, --offset <number>", "Offset for paginating Pro block results", "0")
  .option("--json", "Output as JSON")
  .action(
    async (
      query: string,
      opts: { plan?: string; category?: string; limit: string; offset: string; json?: boolean },
    ) => {
      const parsedLimit = Math.min(Math.max(parseInt(opts.limit, 10) || 20, 1), 50);
      const parsedOffset = Math.max(parseInt(opts.offset, 10) || 0, 0);

      if (opts.plan && opts.plan !== "free" && opts.plan !== "pro") {
        program.error(`Invalid plan "${opts.plan}". Must be "free" or "pro".`);
      }

      await search(query, {
        plan: opts.plan as "free" | "pro" | undefined,
        category: opts.category,
        limit: parsedLimit,
        offset: parsedOffset,
        json: opts.json,
      });
    },
  );

program
  .command("update")
  .description("Update Starwind components to their latest versions")
  .argument("[components...]", "The components to update (space separated)")
  .allowExcessArguments()
  .option("-a, --all", "Update all installed components")
  .option("-y, --yes", "Skip confirmation prompts")
  .addOption(
    new Option("-m, --package-manager <pm>", "Package manager to use").choices([
      "npm",
      "pnpm",
      "yarn",
    ]),
  )
  .action(update);

program
  .command("remove")
  .description("Remove Starwind components from your project")
  .argument("[components...]", "The components to remove (space separated)")
  .allowExcessArguments()
  .option("-a, --all", "Remove all installed components")
  .action(remove);

program
  .command("setup")
  .description("Setup Starwind Pro in your project")
  .option("-y, --yes", "Skip confirmation prompts")
  .addOption(
    new Option("-m, --package-manager <pm>", "Package manager to use").choices([
      "npm",
      "pnpm",
      "yarn",
    ]),
  )
  .option("-p, --pro", "Setup Starwind Pro (default)")
  .action(setup);

program.parse(process.argv);
